<template>
  <div class="login-wrapper">
    <div class="card authentication-card">
      <div class="card-body">
        <!-- <div class="logo-auth-container mb-4">
          <img v-if="franchiseLogo" :src="franchiseLogo" alt="Logo" class="logo-auth-img" />
          <h2 v-else-if="franchiseName" class="franchise-name-title text-black fw-bold">{{ franchiseName }}</h2>
        </div> -->
        <h4 class="text-black fw-bold text-center mb-4">
          {{ $t('auth.login') }}
        </h4>
        <form @submit.prevent="authLogin">
          <div class="form-group mb-4">
            <label class="d-block text-black fw-semibold mb-2">
              {{ $t('auth.email') }}
            </label>
            <input
                type="email"
                class="form-control"
                placeholder="e.g. adam127704@gmail.com"
                v-model="userData.email"
                @change="(event) => handleInput(event, 'email')"
                required
            />
          </div>
          <div class="form-group mb-4">
            <label class="d-block text-black fw-semibold mb-2">
              {{ $t('auth.password') }}
            </label>
            <div class="position-relative">
              <input
                  :type="isPasswordVisible ? 'text' : 'password'"
                  class="form-control pe-5"
                  placeholder="**************"
                  v-model="userData.password"
                  @change="(event) => handleInput(event, 'password')"
                  required
              />
              <button
                  type="button"
                  class="password-toggle-btn border-0 bg-transparent position-absolute top-50 translate-middle-y end-0 me-2 p-0"
                  @click="isPasswordVisible = !isPasswordVisible"
              >
                <i 
                  class="ph-bold" 
                  :class="isPasswordVisible ? 'ph-eye-slash' : 'ph-eye'"
                ></i>
              </button>
            </div>
          </div>

          <div v-if="errorMessage" class="alert-security-msg mb-4">
            <i class="ph-bold ph-warning-circle"></i>
            <span>{{ errorMessage }}</span>
          </div>
          
          <div class="auth-footer-line">
            <div class="form-check mb-0">
              <input
                  class="form-check-input"
                  type="checkbox"
                  id="remember-me"
              />
              <label class="form-check-label text-muted fs-14" for="remember-me">
                {{ $t('auth.rememberMe') }}
              </label>
            </div>
            <router-link
                to="/password-oublie"
                class="forgot-password-link"
            >
              {{ $t('auth.forgotPassword') }}
            </router-link>
          </div>

          <LoaderComponent v-if="isLoading" />

          <button
              v-else
              class="vabene-btn w-100 mt-2"
              type="submit"
              :disabled="!isFormValid"
          >
            {{ $t('auth.loginButton') }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { loginCheck, getUserData } from "@/service/api";
import LoaderComponent from "@/components/Loading/Loader.vue";
import { useToast } from "vue-toastification";
import {UserGeneralKey, UserRole} from "@/models/user.generalkey";
import { debugToken, clearInvalidToken } from "@/utils/debug-token";

export default {
  name: "LoginForm",
  components: { LoaderComponent },
  data(){
    return{
      userData: {
        email: '',
        password: ''
      },
      isLoading: false,
      isPasswordVisible: false,
      errorMessage: null,
    }
  },
  methods: {
    gotoHome(role){
      const dashboardRoute = UserGeneralKey.getDashboardRoute(role);
      this.$router.push(dashboardRoute);
    },
    async authLogin() {
      this.isLoading = true;
      try {
        clearInvalidToken();
        
        const response = await loginCheck(this.userData);
        if (response.code === 200) {
          const token = response.token || response.data?.token || response.data?.access_token;
          
          if (token) {
            localStorage.setItem(UserGeneralKey.USER_TOKEN, token);
            debugToken(); 
            this.toast.success(response.message);
            
            const userDataLoaded = await this.getUserData();
            
            if (!userDataLoaded) {
              this.toast.error('Erreur lors du chargement de votre profil');
              return;
            }
            
            await new Promise(resolve => setTimeout(resolve, 100));
            
            const savedRole = UserGeneralKey.getUserRole();
            
            if (!savedRole) {
              this.toast.error('Erreur: rôle utilisateur introuvable');
              return;
            }
            
            const isFranchise = UserGeneralKey.isFranchise(savedRole);
            const dashboardRoute = UserGeneralKey.getDashboardRoute(savedRole);
            
            sessionStorage.setItem('DEBUG_LOGIN', JSON.stringify({
              role: savedRole,
              isFranchise: isFranchise,
              route: dashboardRoute,
              timestamp: new Date().toISOString()
            }));
            
            this.$router.push(dashboardRoute);
            
          } else {
            this.toast.error(this.$t('auth.missingToken'));
          }
        } else {
          this.toast.error(response.message);
        }
      } catch (error) {
        if (error.response?.status === 429) {
          this.errorMessage = "Sécurité : Trop de tentatives. Votre accès est temporairement restreint pour 1 minute.";
        } else if (error.response?.data?.message === 'Invalid credentials.' || error.response?.status === 401 || error.response?.status === 404) {
          this.errorMessage = "Identifiants incorrects. Veuillez vérifier votre email et mot de passe.";
        } else {
          this.errorMessage = error.response?.data?.message || "Une erreur est survenue lors de la connexion.";
        }
        
        if (this.errorMessage) {
          this.toast.error(this.errorMessage);
        }
      } finally {
        this.isLoading = false;
      }
    },
    async getUserData() {
      this.isLoading = true;
      try {
        const token = localStorage.getItem(UserGeneralKey.USER_TOKEN);
        
        if (!token || token === 'undefined' || token === 'null') {
          this.toast.error(this.$t('auth.missingToken'));
          return false;
        }
        
        const response = await getUserData();
        if (response.code === 200 && response.data) {
          if (!response.data.roles || !Array.isArray(response.data.roles) || response.data.roles.length === 0) {
            this.toast.error('Erreur: aucun rôle utilisateur trouvé');
            return false;
          }
          
          const roleBeforeSave = response.data.roles[0];
          sessionStorage.setItem('DEBUG_GETUSERDATA_BEFORE', JSON.stringify({
            role: roleBeforeSave,
            allRoles: response.data.roles,
            timestamp: new Date().toISOString()
          }));
          
          try {
            UserGeneralKey.saveUserDatA(response.data);
          } catch (error) {
            this.toast.error('Erreur lors de la sauvegarde de votre profil');
            return false;
          }
          
          await new Promise(resolve => setTimeout(resolve, 50));
          
          const savedRole = UserGeneralKey.getUserRole();
          sessionStorage.setItem('DEBUG_GETUSERDATA_AFTER', JSON.stringify({
            role: savedRole,
            timestamp: new Date().toISOString()
          }));
          
          if (!savedRole) {
            this.toast.error('Erreur: impossible de sauvegarder votre rôle');
            return false;
          }
        
          
          return true;
        } else {
          this.toast.error(response.message || 'Erreur lors de la récupération des données utilisateur');
          return false;
        }
      } catch (error) {
        if (error.response && error.response.data && error.response.data.message) {
          this.toast.error(error.response.data.message);
        } else {
          this.toast.error('Erreur lors de la récupération des données utilisateur');
        }
        return false;
      } finally {
        this.isLoading = false;
      }
    },
    handleInput(event, type) {
      const valueText = event.target.value;
      switch (type){
        case 'email':
          this.userData.email = valueText
          this.validEmail(valueText)
          break
        case 'password':
          this.userData.password = valueText
          this.validPassword(valueText)
          break
      }
    },
    validEmail(email) {
      if(email){
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email.trim())
      }

    },
    validPassword(password){
      if (password){
        return password.trim().length > 0
      }

    }
  },
  computed: {
    isFormValid() {
      return (
          this.validEmail(this.userData.email) &&
          this.validPassword(this.userData.password)
      );
    },
    franchiseLogo() {
      const logo = stateStore.franchiseLogo;
      if (logo && logo !== 'null' && logo !== 'undefined' && logo !== '') {
        return logo;
      }
      return null;
    },
    franchiseName() {
      return stateStore.franchiseName;
    },
  },
  setup() {
    const toast = useToast();

    return { toast }
  },
  async mounted() {
    // Vérification du Ghost Mode (Token passé en URL par l'app Support)
    const urlParams = new URLSearchParams(window.location.search);
    const ghostToken = urlParams.get('ghost_token');
    
    if (ghostToken) {
      localStorage.setItem(UserGeneralKey.USER_TOKEN, ghostToken);
      this.toast.info("Session Support active (Ghost Mode)");
      const userDataLoaded = await this.getUserData();
      if (userDataLoaded) {
        const savedRole = UserGeneralKey.getUserRole();
        const dashboardRoute = UserGeneralKey.getDashboardRoute(savedRole);
        this.$router.push(dashboardRoute);
        return;
      }
    }

    const token = localStorage.getItem(UserGeneralKey.USER_TOKEN);
    if (token === 'undefined' || token === 'null' || !token) {
      localStorage.removeItem(UserGeneralKey.USER_TOKEN);
      return;
    }
    
    if(UserGeneralKey.isTokenValid()){
      let savedRole = UserGeneralKey.getUserRole();
      
      if (!savedRole) {
        const userDataLoaded = await this.getUserData();
        if (userDataLoaded) {
          savedRole = UserGeneralKey.getUserRole();
        }
      }
      
      if (savedRole) {
        const dashboardRoute = UserGeneralKey.getDashboardRoute(savedRole);
        this.$router.push(dashboardRoute);
      }
    }
  }
};
</script>

<style scoped>
.login-wrapper {
  width: 100%;
  padding: 20px;
}

.authentication-card {
  width: 100% !important;
  max-width: 460px !important;
  margin: 60px auto !important;
  background: white !important;
  border: none !important;
  border-radius: 12px !important;
  box-shadow: 0 20px 50px rgba(0,0,0,0.1) !important;
  padding: 0 !important;
}

.card-body {
  padding: 30px !important;
}

.logo-auth-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-bottom: 25px;
}

.logo-auth-img {
  max-height: 110px;
  max-width: 100%;
  object-fit: contain;
}

.franchise-name-title {
  font-size: 24px;
  text-align: center;
  margin: 0;
  color: #388e3c !important;
}

.text-center {
  text-align: center !important;
  white-space: nowrap !important;
}

.form-control {
  background-color: #f0f7ff !important;
  border: 1px solid #d1d9e6 !important;
  padding: 14px !important;
  color: #000 !important;
  border-radius: 6px !important;
}

.form-control:focus {
  background-color: #fff !important;
  border-color: #388e3c !important;
  box-shadow: 0 0 0 3px rgba(56, 142, 60, 0.1) !important;
}

.auth-footer-line {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  margin-bottom: 25px !important;
  width: 100% !important;
}

.vabene-btn {
  background-color: #388e3c !important;
  color: white !important;
  padding: 15px !important;
  font-weight: 600 !important;
  font-size: 16px !important;
  border-radius: 8px !important;
  border: none !important;
  transition: all 0.3s ease !important;
  cursor: pointer !important;
}

.vabene-btn:hover:not(:disabled) {
  background-color: #2e7d32 !important;
  box-shadow: 0 4px 12px rgba(46, 125, 50, 0.2) !important;
}

.vabene-btn:disabled {
  background-color: #a5d6a7 !important;
  opacity: 0.8 !important;
  cursor: not-allowed !important;
}

.forgot-password-link {
  color: #388e3c !important;
  text-decoration: underline !important;
  font-size: 14px !important;
  font-weight: 500 !important;
}

.fs-14 { font-size: 14px; }

.password-toggle-btn {
  color: #6c757d;
  font-size: 18px;
}
.alert-security-msg {
  background: rgba(220, 53, 69, 0.08);
  border-left: 3px solid #dc3545;
  color: #dc3545;
  padding: 12px 15px;
  border-radius: 6px;
  font-size: 13.5px;
  display: flex;
  align-items: center;
  gap: 10px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>